import { PreviewMeeting } from "@/schema"
import { format } from "date-fns"
import { Calendar, ExternalLink, Link, Users } from "lucide-react"

import { usePeekViewData } from "@/hooks/api/peek-views/queries"
import { Link as BookLink } from "@/components/book-link"
import { MeetingTypeBadge } from "@/components/meetings/meeting-type-selector"

import PeekViewSkeleton from "./skeleton"

export default function MeetingPeekView({ meetingId, href }: { meetingId: string; href: string | null }) {
  const { data: meeting, isLoading } = usePeekViewData(meetingId, "Meeting") as {
    data: PreviewMeeting
    isLoading: boolean
  }

  if (isLoading) return <PeekViewSkeleton className="max-w-80" />

  const dateTimeText = `${format(meeting.start_time, "MMM d, yyyy · h:mm a")}${meeting.end_time ? ` - ${format(meeting.end_time, "h:mm a")}` : ""}`

  return (
    <div className="text-tertiary-foreground flex w-full max-w-80 flex-col items-stretch gap-2">
      <div className="mb-0.5 flex items-baseline">
        <p className="truncate font-medium">{meeting.title || "Untitled meeting"}</p>
        {href && (
          <BookLink
            href={href}
            className="text-muted-foreground hover:text-link ml-2 transition-colors"
            onClick={(e) => e.stopPropagation()}
            aria-label="Open in new page"
          >
            <ExternalLink className="size-3.5" strokeWidth={1.5} />
          </BookLink>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Calendar className="text-icon size-4 flex-none" />
        <p className="text-sm">{dateTimeText}</p>
        <MeetingTypeBadge meetingType={meeting.meeting_type} />
      </div>
      <div className="flex items-center gap-2">
        <Link className="text-icon size-4 flex-none" />
        {meeting.meeting_link ? (
          <a
            href={meeting.meeting_link}
            className="text-link hover:text-link-hover max-w-sm truncate underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {meeting.meeting_link}
          </a>
        ) : (
          <p className="text-sm">No conference link</p>
        )}
      </div>
      <div className="flex items-start gap-2">
        <Users className="text-icon mt-0.5 size-4 flex-none" />
        <p className="text-sm">
          {meeting.participants?.map((participant) => participant.email).join(", ") || "No participants"}
        </p>
      </div>
    </div>
  )
}
